// =======================================================
// Entrevistas de admisión (diseño A, oct. 2026): a la izquierda quién espera
// fecha (y las que ya pasaron sin resultado), en el centro la semana con cada
// entrevista a su hora y a la derecha la ficha del candidato elegido.
// =======================================================
const EntrevistasView = {
    preRegistrations: [],
    selectedId: null,
    weekStart: null, // lunes de la semana a la vista (YYYY-MM-DD)
    _loaded: false,
    _loadError: false,

    // Status mappings
    statuses: {
        'entrevista_solicitada': { label: 'Entrevista solicitada', color: '#6d28d9', bg: '#ede9fe' },
        'entrevista_superada': { label: 'Entrevista superada', color: '#581c87', bg: '#d8b4fe' },
        'pendiente': { label: 'Pendiente', color: '#64748b', bg: '#f1f5f9' },
        'no_continua': { label: 'No continúa', color: '#b91c1c', bg: '#fee2e2' }
    },

    _pad(n) { return String(n).padStart(2, '0'); },
    _iso(d) { return `${d.getFullYear()}-${this._pad(d.getMonth() + 1)}-${this._pad(d.getDate())}`; },
    _date(iso) { const [y, m, d] = String(iso).split('-').map(Number); return new Date(y, m - 1, d); },
    _today() { return this._iso(new Date()); },
    _monday(iso) { const d = this._date(iso); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return this._iso(d); },
    _addDays(iso, n) { const d = this._date(iso); d.setDate(d.getDate() + n); return this._iso(d); },
    _esc(s) { return Utils.sanitizeHTML(String(s == null ? '' : s)); },
    _name(s) { return [s.firstName, s.lastName1, s.lastName2].filter(Boolean).join(' '); },
    canManage() { return AuthManager.canManageModule('pre-matricula', ['admin', 'coordinator', 'rrhh']); },

    async init() {
        this.preRegistrations = [];
        this.selectedId = null;
        this.weekStart = this._monday(this._today());
        this._loaded = false;
        await this.loadData();
        this.paint();
    },

    async loadData() {
        this._loadError = false;
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/pre-registration', { headers: { 'X-SICAF-CSRF': token } });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'No se pudieron cargar las entrevistas');
            this.preRegistrations = data.preRegistrations || [];
            this.updateSidebarBadge();
        } catch (e) {
            console.error('Error loading interviews data:', e);
            this._loadError = true;
        }
        this._loaded = true;
    },

    updateSidebarBadge() {
        const pending = this.interviews().length;
        const badge = document.getElementById('entrevistas-sidebar-badge');
        if (badge) {
            if (pending > 0) {
                badge.innerText = pending;
                badge.style.display = 'inline-block';
            } else {
                badge.style.display = 'none';
            }
        }
    },

    // Solicitudes aprobadas con la entrevista pedida (las de la web sin aprobar y las plazas provisionales no cuentan).
    interviews() {
        return this.preRegistrations.filter(s => s.status === 'entrevista_solicitada' && !s.isDb && !s.isProvisional && !(s.firstName === 'PLAZA' && s.lastName1 === 'PENDIENTE'));
    },
    _scheduled(s) { return !!(s.interviewDate && s.interviewTime); },

    async changeStatus(id, newStatus) {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        // Solicitud online sin aprobar: no hay entrevista hasta aprobarla (el servidor lo rechaza igualmente).
        if (id.startsWith('db_')) {
            SafeHTML.fire('Aprueba primero la solicitud', 'Hasta aprobarla (✓) en Prematrícula no se pueden programar entrevistas, abrir el pago ni cambiar su estado.', 'info');
            return;
        }
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/pre-registration/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': token },
                body: JSON.stringify({ status: newStatus })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);
            Utils.showToast(newStatus === 'entrevista_superada' ? 'Entrevista superada. Notificación enviada a Administración y RRHH.' : 'Estado actualizado', 'success');
            if (this.selectedId === id) this.selectedId = null;
            await this.loadData();
            this.paint();
        } catch (e) {
            console.error('Error updating status:', e);
            SafeHTML.fire('Error', 'No se pudo actualizar el estado', 'error');
        }
    },

    async saveInterviewDateTime(id, date, time) {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        if (id.startsWith('db_')) { SafeHTML.fire('Aprueba primero la solicitud', 'Hasta aprobarla (✓) en Prematrícula no se pueden programar entrevistas, abrir el pago ni cambiar su estado.', 'info'); return; }
        if (!date || !time) {
            SafeHTML.fire('Falta la fecha o la hora', 'Elige el día y la hora de la entrevista.', 'warning');
            return;
        }
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/pre-registration/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': token },
                body: JSON.stringify({ interviewDate: date, interviewTime: time })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);
            Utils.showToast(data.emailSent ? 'Entrevista agendada y citación enviada por correo' : 'Entrevista agendada', 'success');
            this.weekStart = this._monday(date);
            await this.loadData();
            this.paint();
        } catch(e) {
            console.error(e);
            SafeHTML.fire('Error', 'No se pudo guardar la fecha de la entrevista', 'error');
        }
    },

    deleteInterview(id) {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        const interview = this.preRegistrations.find(s => s.id === id);
        const name = interview ? this._name(interview) : 'esta entrevista';
        SafeHTML.fire({
            title: '¿Eliminar la entrevista?',
            html: `Se eliminará la solicitud de <b>${Utils.sanitizeHTML(name)}</b>. No se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#8E2A21',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                const token = AuthManager.getCsrfToken();
                const res = await fetch(`/api/pre-registration/${id}`, { method: 'DELETE', headers: { 'X-SICAF-CSRF': token } });
                const resData = await res.json();
                if (!resData.success) { SafeHTML.fire('Error', resData.error || 'No se pudo eliminar la entrevista', 'error'); return; }
                Utils.showToast('Entrevista eliminada', 'success');
                if (this.selectedId === id) this.selectedId = null;
                await this.loadData();
                this.paint();
            } catch (e) {
                console.error('Error al eliminar entrevista:', e);
                SafeHTML.fire('Error', 'Error de red al eliminar la entrevista', 'error');
            }
        });
    },

    select(id) {
        this.selectedId = id;
        const s = this.interviews().find(x => x.id === id);
        if (s && s.interviewDate) this.weekStart = this._monday(s.interviewDate);
        this.paint();
    },
    changeWeek(delta) { this.weekStart = this._addDays(this.weekStart, delta * 7); this.paint(); },
    goToday() { this.weekStart = this._monday(this._today()); this.paint(); },
    saveSelected() {
        const date = document.getElementById('ev-date-input'), time = document.getElementById('ev-time-input');
        if (this.selectedId) this.saveInterviewDateTime(this.selectedId, date ? date.value : '', time ? time.value : '');
    },

    render() {
        return `
        <div class="view-container dnr-proposal entrevistas-view" style="padding-top: 1rem;">
            <header class="ev-top">
                <h1 class="ev-title">Entrevistas</h1>
                <div class="ev-week-nav">
                    <button type="button" aria-label="Semana anterior" ${UIEvents.attr("click", function(event,captured){EntrevistasView.changeWeek(-1);}, [])}><i class="fas fa-chevron-left"></i></button>
                    <span id="ev-week-label"></span>
                    <button type="button" aria-label="Semana siguiente" ${UIEvents.attr("click", function(event,captured){EntrevistasView.changeWeek(1);}, [])}><i class="fas fa-chevron-right"></i></button>
                </div>
                <span id="ev-today-slot"></span>
                <span class="ev-spacer"></span>
                <span id="ev-pills" class="ev-pills"></span>
                <button type="button" class="ev-btn" ${UIEvents.attr("click", function(event,captured){App.navigate('pre-matricula');}, [])}>Pre-Matrícula <i class="fas fa-arrow-right"></i></button>
            </header>
            <div id="ev-body"><p class="ev-empty">Cargando entrevistas…</p></div>
        </div>`;
    },

    paint() {
        const body = document.getElementById('ev-body');
        if (!body) return;
        if (!this.weekStart) this.weekStart = this._monday(this._today());
        const esc = s => this._esc(s);
        const today = this._today();
        const all = this.interviews();
        const toSchedule = all.filter(s => !this._scheduled(s));
        const scheduled = all.filter(s => this._scheduled(s)).sort((a, b) => `${a.interviewDate} ${a.interviewTime}`.localeCompare(`${b.interviewDate} ${b.interviewTime}`));
        const overdue = scheduled.filter(s => s.interviewDate < today);

        const label = document.getElementById('ev-week-label');
        const weekEnd = this._addDays(this.weekStart, 6);
        const fmt = (iso, opts) => this._date(iso).toLocaleDateString('es-ES', opts);
        if (label) label.textContent = `${fmt(this.weekStart, { day: 'numeric', month: 'short' })} – ${fmt(weekEnd, { day: 'numeric', month: 'short' })}`;
        SafeHTML.set(document.getElementById('ev-today-slot'), this.weekStart === this._monday(today) ? '' :
            `<button type="button" class="ev-btn" ${UIEvents.attr("click", function(event,captured){EntrevistasView.goToday();}, [])}>Hoy</button>`);
        SafeHTML.set(document.getElementById('ev-pills'), this._loaded && !this._loadError ? `
            <span class="ev-pill ${toSchedule.length ? 'is-warn' : ''}">${toSchedule.length} por agendar</span>
            <span class="ev-pill is-ok">${scheduled.length - overdue.length} agendadas</span>
            ${overdue.length ? `<span class="ev-pill is-bad">${overdue.length} sin resultado</span>` : ''}` : '');

        if (this._loadError) {
            SafeHTML.set(body, `<div class="ev-empty" role="alert"><p>No se pudieron cargar las entrevistas.</p><button type="button" class="ev-btn" ${UIEvents.attr("click", function(event,captured){EntrevistasView.init();}, [])}>Reintentar</button></div>`);
            return;
        }
        if (!all.length) {
            SafeHTML.set(body, `<div class="ev-empty"><h3>No hay entrevistas pendientes</h3><p>Las solicitudes marcadas como «Entrevista solicitada» en Pre-Matrícula aparecen aquí para darles fecha y hora.</p>
                <button type="button" class="ev-btn" ${UIEvents.attr("click", function(event,captured){App.navigate('pre-matricula');}, [])}>Ir a Pre-Matrícula</button></div>`);
            return;
        }
        if (!all.some(s => s.id === this.selectedId)) {
            const inWeek = scheduled.find(s => s.interviewDate >= this.weekStart && s.interviewDate <= weekEnd);
            this.selectedId = (toSchedule[0] || overdue[0] || inWeek || scheduled[0]).id;
        }
        const pick = id => UIEvents.attr("click", function(event,captured){EntrevistasView.select(String(captured[0]));}, [id]);
        const since = s => {
            const created = s.createdAt || s.updatedAt;
            if (!created) return '';
            const days = Math.floor((Date.now() - new Date(created).getTime()) / 86400000);
            return isNaN(days) ? '' : days <= 0 ? 'Pidió entrevista hoy' : days === 1 ? 'Hace 1 día' : days < 14 ? `Hace ${days} días` : `Hace ${Math.floor(days / 7)} semanas`;
        };
        const item = (s, extra) => `<button type="button" class="ev-item ${s.id === this.selectedId ? 'is-sel' : ''}" aria-pressed="${s.id === this.selectedId}" ${pick(s.id)}>
            <b>${esc(this._name(s))}</b><span>${esc(s.courseName || '')}</span>${extra ? `<small>${esc(extra)}</small>` : ''}</button>`;

        // Semana: de lunes a viernes, y sábado o domingo solo si hay entrevistas ese día.
        const days = [];
        for (let i = 0; i < 7; i++) {
            const iso = this._addDays(this.weekStart, i);
            const items = scheduled.filter(s => s.interviewDate === iso);
            if (i < 5 || items.length) days.push({ iso, items });
        }
        const weekCount = days.reduce((n, d) => n + d.items.length, 0);
        const nextOne = scheduled.find(s => s.interviewDate > weekEnd);

        const sel = all.find(s => s.id === this.selectedId);
        const isOverdue = sel && this._scheduled(sel) && sel.interviewDate < today;
        const state = !sel ? '' : !this._scheduled(sel) ? 'Por agendar'
            : `${fmt(sel.interviewDate, { weekday: 'short', day: 'numeric', month: 'short' })} · ${sel.interviewTime}${isOverdue ? ' · ya pasó' : ''}`;
        const can = this.canManage();
        const phone = sel && (sel.phone || sel.telefono);

        SafeHTML.set(body, `
            <div class="ev-layout">
                <aside class="ev-card ev-side">
                    <div class="ev-head is-warn"><span class="ev-label">Por agendar · ${toSchedule.length}</span></div>
                    <div class="ev-list">${toSchedule.length ? toSchedule.map(s => item(s, since(s))).join('') : '<p class="ev-note">Todas tienen fecha.</p>'}</div>
                    ${overdue.length ? `<div class="ev-head is-bad"><span class="ev-label">Ya pasó la fecha · marca el resultado</span></div>
                    <div class="ev-list">${overdue.map(s => item(s, `${fmt(s.interviewDate, { weekday: 'short', day: 'numeric' })} · ${s.interviewTime}`)).join('')}</div>` : ''}
                </aside>
                <section class="ev-card ev-week">
                    <div class="ev-days" style="grid-template-columns: repeat(${days.length}, minmax(0, 1fr));">
                        ${days.map(d => `
                            <div class="ev-day ${d.iso === today ? 'is-today' : ''}">
                                <div class="ev-day-head"><b>${esc(fmt(d.iso, { weekday: 'short', day: 'numeric' }))}</b>${d.iso === today ? '<span>hoy</span>' : ''}</div>
                                <div class="ev-day-body">
                                    ${d.items.map(s => `<button type="button" class="ev-slot ${s.id === this.selectedId ? 'is-sel' : ''} ${d.iso < today ? 'is-past' : ''}" aria-pressed="${s.id === this.selectedId}" ${pick(s.id)}>
                                        <span class="ev-slot-time">${esc(s.interviewTime)}</span><b>${esc(this._name(s))}</b><span>${esc(s.courseName || '')}</span></button>`).join('')}
                                </div>
                            </div>`).join('')}
                    </div>
                    ${weekCount ? '' : `<p class="ev-note ev-week-empty">Esta semana no hay entrevistas.${nextOne ? ` La siguiente es el ${esc(fmt(nextOne.interviewDate, { weekday: 'long', day: 'numeric', month: 'long' }))}.` : ''}</p>`}
                </section>
                <aside class="ev-card ev-detail" id="ev-detail">
                    ${sel ? `
                    <div class="ev-detail-head">
                        <span class="ev-label ${isOverdue ? 'is-bad' : ''}">${esc(state)}</span>
                        <h2>${esc(this._name(sel))}</h2>
                        <span class="ev-course">${esc(sel.courseName || '')}</span>
                    </div>
                    <div class="ev-detail-body">
                        ${sel.email || phone ? `<div class="ev-contact">${sel.email ? `<a href="mailto:${esc(sel.email)}"><i class="far fa-envelope"></i> ${esc(sel.email)}</a>` : ''}${phone ? `<a href="tel:${esc(String(phone).replace(/\s+/g, ''))}"><i class="fas fa-phone"></i> ${esc(phone)}</a>` : ''}</div>` : ''}
                        ${sel.notes ? `<p class="ev-notes">${esc(sel.notes)}</p>` : ''}
                        <span class="ev-label">Fecha y hora</span>
                        <div class="ev-when">
                            <input type="date" id="ev-date-input" aria-label="Fecha de la entrevista" value="${esc(sel.interviewDate || '')}" ${can ? '' : 'disabled'}>
                            <input type="time" step="900" id="ev-time-input" aria-label="Hora de la entrevista" value="${esc(sel.interviewTime || '')}" ${can ? '' : 'disabled'}>
                        </div>
                        ${can ? `<button type="button" class="ev-btn is-primary" ${UIEvents.attr("click", function(event,captured){EntrevistasView.saveSelected();}, [])}>${this._scheduled(sel) ? 'Cambiar fecha y avisar' : 'Agendar y enviar citación'}</button>
                        <small class="ev-note">Al guardar se le envía la citación por correo.</small>` : '<small class="ev-note">Solo consulta.</small>'}
                    </div>
                    ${can ? `<div class="ev-detail-foot">
                        <button type="button" class="ev-btn is-ok" ${UIEvents.attr("click", function(event,captured){EntrevistasView.changeStatus(String(captured[0]),'entrevista_superada');}, [sel.id])}><i class="fas fa-check"></i> Entrevista superada</button>
                        <button type="button" class="ev-btn is-danger" ${UIEvents.attr("click", function(event,captured){EntrevistasView.deleteInterview(String(captured[0]));}, [sel.id])}>Eliminar</button>
                    </div>` : ''}` : ''}
                </aside>
            </div>`);
    }
};

window.EntrevistasView = EntrevistasView;
